import React, { useEffect, useState } from 'react';
import { VStack, HStack, Box } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { parsePhoneNumber } from 'libphonenumber-js';

import DialogBox from '../../components/shared/dialogs/DialogBox';
import InputBox from '../../components/shared/fields/InputBox';
import ContactBox from '../../components/shared/fields/ContactBox';
import { createUserRequest, setCreateUserButtonLoading } from '../../store/userSlice';

const CustomerCreationDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { loading, createUserButtonLoading, error: userError } = useSelector((state) => state.user);
  const [emailError, setEmailError] = useState(null);

  const customerSchema = z.object({
    firstName: z
      .string()
      .nonempty({ message: 'First Name is required' })
      .min(2, { message: 'At least 2 characters' })
      .max(50, { message: 'At most 50 characters' })
      .regex(/^[A-Za-z]+$/, 'Only letters'),
    lastName: z
      .string()
      .nonempty({ message: 'Last Name is required' })
      .min(2, { message: 'At least 2 characters' })
      .max(50, { message: 'At most 50 characters' }),
    mobileNumber: z
      .string()
      .nonempty({ message: 'Phone Number is required' })
      .regex(/^\+?\d{10,15}$/, { message: 'Enter a valid phone number' }),
    email: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .email('Invalid email')
    ),
    address1: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .min(2, { message: 'At least 2 characters' })
        .max(100, { message: 'At most 100 characters' })
        .optional()
    ),
    address2: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .min(2, { message: 'At least 2 characters' })
        .max(100, { message: 'At most 100 characters' })
        .optional()
    ),
    landmark: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .min(2, { message: 'At least 2 characters' })
        .max(100, { message: 'At most 100 characters' })
        .optional()
    ),
    city: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .min(2, { message: 'At least 2 characters' })
        .max(100, { message: 'At most 100 characters' })
        .optional()
    ),
    state: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .min(2, { message: 'At least 2 characters' })
        .max(100, { message: 'At most 100 characters' })
        .optional()
    ),
    zipCode: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .regex(/^\d{5,}$/, { message: 'Enter a valid zip code' })
        .optional(),
    ),
    country: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .min(2, { message: 'At least 2 characters' })
        .max(100, { message: 'At most 100 characters' })
        .optional()
    ),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(customerSchema),
    mode: 'onBlur',
  });

  const emailValue = watch('email');

  useEffect(() => {
    if (!open) {
      reset({
        firstName: '',
        lastName: '',
        mobileNumber: '',
        email: '',
        address1: '',
        address2: '',
        landmark: '',
        state: '',
        zipCode: '',
        country: '',
        city: ''
      });
      dispatch(setCreateUserButtonLoading(false));
      setEmailError(null);
    }
  }, [open, reset, dispatch]);

  useEffect(() => {
    setEmailError(null);
  }, [emailValue]);

  useEffect(() => {
    if (userError && /email/i.test(userError)) {
      setEmailError(userError);
    }
  }, [userError]);

  const onSubmit = (data) => {
    setEmailError(null);

    let countryCode = "", nationalNumber = "";
    if (data.mobileNumber) {
      const parsed = parsePhoneNumber(data.mobileNumber);
      countryCode = "+" + parsed.countryCallingCode;
      nationalNumber = parsed.nationalNumber;
    }

    const generatedEmail = data.email ||
      `${data.mobileNumber.replace(/[^+\d]/g, '')}+prodyt@product.com`;

    const address = JSON.stringify({
      line1: data.address1,
      line2: data.address2,
      landmark: data.landmark,
      city: data.city,
      state: data.state,
      pincode: data.zipCode,
      country: data.country,
    });

    dispatch(
      createUserRequest({
        email: generatedEmail,
        verifyEmail: data.email || null,
        firstName: data.firstName,
        lastName: data.lastName,
        localPhoneNo: nationalNumber,
        phoneNumber: data.mobileNumber,
        mobileCountryCode: countryCode,
        address: address,
        platform: 'app',
        source: 'customer',
      })
    );
  };

  const RequiredAsterisk = () => <span style={{ color: 'red' }}>*</span>;

  const dialogBody = (
    <VStack align="stretch" spacing={4}>
      <HStack spacing={4}>
        <InputBox
          label={<Box>First Name <RequiredAsterisk /></Box>}
          placeholder="Amit"
          register={register('firstName')}
          invalid={!!errors.firstName}
          errorText={errors.firstName?.message}
        />
        <InputBox
          label={<Box>Last Name <RequiredAsterisk /></Box>}
          placeholder="Kumar"
          register={register('lastName')}
          invalid={!!errors.lastName}
          errorText={errors.lastName?.message}
        />
      </HStack>
      <InputBox
        label={<Box>Email<RequiredAsterisk /></Box>}
        placeholder="amitkumar@gmail.com"
        register={register('email')}
        invalid={!!errors.email || !!emailError}
        errorText={errors.email?.message || emailError}
      />
      <ContactBox
        label={<Box>Phone <RequiredAsterisk /></Box>}
        register={register('mobileNumber')}
        invalid={!!errors.mobileNumber}
        errorText={errors.mobileNumber?.message}
      />
      <InputBox
        label="Address Line 1"
        placeholder="23, Gandhi Lane"
        register={register('address1')}
        invalid={!!errors.address1}
        errorText={errors.address1?.message}
      />
      <InputBox
        label="Address Line 2"
        placeholder="Chandani Chowk"
        register={register('address2')}
        invalid={!!errors.address2}
        errorText={errors.address2?.message}
      />
      <InputBox
        label="Landmark"
        placeholder="Near Central Park"
        register={register('landmark')}
        invalid={!!errors.landmark}
        errorText={errors.landmark?.message}
      />
      <InputBox
        label="City"
        placeholder="Delhi"
        register={register('city')}
        invalid={!!errors.city}
        errorText={errors.city?.message}
      />
      <InputBox
        label="State"
        placeholder="New Delhi"
        register={register('state')}
        invalid={!!errors.state}
        errorText={errors.state?.message}
      />
      <InputBox
        label="Zip Code"
        placeholder="234503"
        register={register('zipCode')}
        invalid={!!errors.zipCode}
        errorText={errors.zipCode?.message}
      />
      <InputBox
        label="Country"
        placeholder="India"
        register={register('country')}
        invalid={!!errors.country}
        errorText={errors.country?.message}
      />
    </VStack>
  );

  const handleClose = () => {
    dispatch(setCreateUserButtonLoading(false));
    setEmailError(null);
    onClose();
  };

  return (
    <DialogBox
      dialog_title="Create Customer"
      dialog_body={dialogBody}
      cancelButtonName="Cancel"
      SaveButtonName={loading || createUserButtonLoading ? 'Creating...' : 'Create Customer'}
      saveButtonOnclick={handleSubmit(onSubmit)}
      open={open}
      onClose={handleClose}
      isSaveDisabled={loading || createUserButtonLoading}
    />
  );
};

export default CustomerCreationDialog;
